import { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Checkbox,
  Chip,
  TextField,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Backdrop,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  createFilterOptions,
  IconButton,
  Tooltip,
  InputBase,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import { useDispatch, useSelector } from "react-redux";
import {
  GetCustomDeductionAction,
  GetCustomEarningAction,
  GetDeductionAction,
  GetEarningAction,
} from "../../../../../store/Finance/Component/Action";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  UpdateCustomComponentHelper,
  GetSalaryComponentSectionHelper,
  UpsertSalaryComponentSectionHelper,
  DeleteSalaryComponentSectionHelper,
  GetSalaryComponentConfigHelper,
  UpsertSalaryComponentConfigHelper,
} from "../../../Finance/helper/Component/ComponentHelper";
import { createStyles, makeStyles } from "@mui/styles";

const filter = createFilterOptions();

const useStyles = makeStyles((theme) =>
  createStyles({
    Backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      position: "absolute",
      borderRadius: "inherit",
    },
  })
);
export function ComponentConfig({ type }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const {
    earning_data,
    deduction_data,
    isEarningsLoading,
    isDeductionLoading,
    custom_earning_data,
    custom_deduction_data,
  } = useSelector((state) => state?.FinanceReducer.ComponentReducer);
  const earningComp = earning_data?.[0]?.fn_get_earning_components || [];
  const deductionComp = deduction_data?.[0]?.fn_get_deduction_components || [];
  const compData =
    type === "Earning"
      ? earningComp
      : type === "Deduction"
      ? deductionComp
      : [];
  const [selectedComponents, setSelectedComponents] = useState([]);
  const [componentConfigs, setComponentConfigs] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);

  const [dbSections, setDbSections] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  const [newSectionInput, setNewSectionInput] = useState("");
  const [isAddingSection, setIsAddingSection] = useState(false);

  const defaultSection = useMemo(
    () => dbSections.find((s) => s.is_default)?.section_name || (type === "Earning" ? "Earnings" : "Deduction"),
    [dbSections, type]
  );

  const filteredComponents = compData?.filter((component) =>
    component?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  const availableSections = useMemo(() => {
    const sections = new Set();
    if (Array.isArray(dbSections)) {
      dbSections.forEach((s) => {
        if (s?.section_name?.trim()) sections.add(s.section_name.trim());
      });
    }
    customSections.forEach((s) => {
      if (s?.trim()) sections.add(s.trim());
    });
    selectedComponents?.forEach((c) => {
      if (c?.section?.trim()) sections.add(c.section.trim());
    });
    Object.values(componentConfigs)?.forEach((c) => {
      if (c?.section?.trim()) sections.add(c.section.trim());
    });
    return Array.from(sections);
  }, [dbSections, customSections, selectedComponents, componentConfigs]);

  const isSectionUsed = (sectionName) => {
    const isUsedInSelected = selectedComponents?.some(
      (c) => c?.section?.trim()?.toLowerCase() === sectionName?.trim()?.toLowerCase()
    );
    const isUsedInConfigs = Object.values(componentConfigs)?.some(
      (c) => c?.section?.trim()?.toLowerCase() === sectionName?.trim()?.toLowerCase()
    );
    return isUsedInSelected || isUsedInConfigs;
  };

  const handleAddSection = async () => {
    const trimmed = newSectionInput.trim();
    if (!trimmed) {
      setIsAddingSection(false);
      return;
    }
    const res = await UpsertSalaryComponentSectionHelper({
      section_name: trimmed,
      component_type: type,
      is_default: false,
    });
    if (!res.hasError) {
      const secRes = await GetSalaryComponentSectionHelper({ component_type: type });
      if (Array.isArray(secRes?.data?.data)) {
        setDbSections(secRes.data.data);
      }
      setCustomSections((prev) => [...prev, trimmed]);
    }
    setNewSectionInput("");
    setIsAddingSection(false);
  };

  const handleDeleteSection = async (sectionName) => {
    const isDefault = dbSections.some((s) => s.section_name === sectionName && s.is_default);
    if (isSectionUsed(sectionName) || isDefault) return;
    const existingDbSec = Array.isArray(dbSections)
      ? dbSections.find(
          (s) => s?.section_name?.toLowerCase() === sectionName.toLowerCase()
        )
      : null;
    if (existingDbSec?.salary_component_section_pk) {
      const res = await DeleteSalaryComponentSectionHelper({
        section_pk: existingDbSec.salary_component_section_pk,
      });
      if (!res.hasError) {
        const secRes = await GetSalaryComponentSectionHelper({ component_type: type });
        if (Array.isArray(secRes?.data?.data)) {
          setDbSections(secRes.data.data);
        }
      }
    }
    setCustomSections((prev) =>
      prev.filter((s) => s.toLowerCase() !== sectionName.toLowerCase())
    );
  };

  const handleComponentToggle = (component) => {
    setSelectedComponents((prev) => {
      const exists = prev?.some((c) => c?.name === component?.name);
      if (exists) {
        return prev?.filter((c) => c?.name !== component?.name);
      }
      const existingConfig = componentConfigs[component?.name];
      const newEntry = {
        name: component?.name,
        label: component?.label,
        custom_label: existingConfig?.custom_label ?? component?.label,
        section: existingConfig?.section ?? defaultSection,
      };
      return [...prev, newEntry];
    });
  };

  const handleLabelChange = (component, newLabel) => {
    setComponentConfigs((prev) => ({
      ...prev,
      [component?.name]: {
        ...prev[component?.name],
        custom_label: newLabel,
      },
    }));
    setSelectedComponents((prev) =>
      prev?.map((c) =>
        c?.name === component?.name ? { ...c, custom_label: newLabel } : c
      )
    );
  };

  const handleSectionChange = (component, newSection) => {
    const trimmed = newSection?.trim();
    if (trimmed && !availableSections.some((s) => s.toLowerCase() === trimmed.toLowerCase())) {
      setCustomSections((prev) => [...prev, trimmed]);
    }
    setComponentConfigs((prev) => ({
      ...prev,
      [component?.name]: {
        ...prev[component?.name],
        section: newSection,
      },
    }));
    setSelectedComponents((prev) =>
      prev?.map((c) =>
        c?.name === component?.name ? { ...c, section: newSection } : c
      )
    );
  };

  const handleSelectAll = () => {
    if (selectedComponents?.length === filteredComponents?.length) {
      setSelectedComponents([]);
    } else {
      const allSelected = filteredComponents?.map((comp) => {
        const existing = selectedComponents?.find((c) => c?.name === comp?.name);
        const config = componentConfigs[comp?.name];
        return {
          name: comp?.name,
          label: comp?.label,
          custom_label: existing?.custom_label ?? config?.custom_label ?? comp?.label,
          section: existing?.section ?? config?.section ?? defaultSection,
        };
      });
      setSelectedComponents(allSelected);
    }
  };

  const fetchBackendData = async () => {
    const secRes = await GetSalaryComponentSectionHelper({ component_type: type });
    const fetchedSections = Array.isArray(secRes?.data?.data) ? secRes.data.data : [];
    if (fetchedSections.length > 0) {
      setDbSections(fetchedSections);
    }
    const currentDefaultSection =
      fetchedSections.find((s) => s.is_default)?.section_name ||
      (type === "Earning" ? "Earnings" : "Deduction");

    const confRes = await GetSalaryComponentConfigHelper({ component_type: type });
    if (Array.isArray(confRes?.data?.data) && confRes.data.data.length > 0) {
      const initialConfigs = {};
      const activeSelected = [];
      const sectionsFromConfigs = [];

      confRes.data.data.forEach((item) => {
        const itemSection = item.section_name || currentDefaultSection;
        if (item.is_active) {
          activeSelected.push({
            name: item.component_name,
            label: item.label,
            custom_label: item.label,
            section: itemSection,
          });
        }
        initialConfigs[item.component_name] = {
          custom_label: item.label,
          section: itemSection,
          salary_component_section_fk: item.salary_component_section_fk,
        };
        const isDefault = fetchedSections.some((s) => s.section_name === itemSection && s.is_default);
        if (itemSection && !isDefault) {
          sectionsFromConfigs.push(itemSection);
        }
      });

      setSelectedComponents(activeSelected);
      setComponentConfigs(initialConfigs);
      if (sectionsFromConfigs.length > 0) {
        setCustomSections((prev) => Array.from(new Set([...prev, ...sectionsFromConfigs])));
      }
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    const payload = selectedComponents.map((c) => ({
      name: c.name,
      label: c.label,
      custom_label: c.custom_label ?? c.label,
      section: c.section || defaultSection,
    }));

    UpdateCustomComponentHelper({
      custom_component_json: JSON.stringify(payload),
      component_type: type,
    }).then((res) => {
      setIsSaving(false);
      if (res?.data?.statuscode === 200) {
        type === "Earning" && dispatch(GetCustomEarningAction());
        type === "Deduction" && dispatch(GetCustomDeductionAction());
        fetchBackendData();
      }
    });
  };

  useEffect(() => {
    type === "Earning" && dispatch(GetEarningAction());
    type === "Deduction" && dispatch(GetDeductionAction());
    type === "Earning" && dispatch(GetCustomEarningAction());
    type === "Deduction" && dispatch(GetCustomDeductionAction());
    fetchBackendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, type]);

  useEffect(() => {
    if (type === "Earning") {
      const saved = custom_earning_data?.[0]?.earnings_json;
      if (Array.isArray(saved) && saved.length > 0) {
        const normalized = saved.map((c) => ({
          name: c.name,
          label: c.label,
          custom_label: c.custom_label ?? c.label,
          section: c.section || defaultSection,
        }));
        setSelectedComponents(normalized);
        const initialConfigs = {};
        const savedSections = [];
        normalized.forEach((c) => {
          initialConfigs[c.name] = {
            custom_label: c.custom_label,
            section: c.section,
          };
          const isDefault = dbSections.some((s) => s.section_name === c.section && s.is_default);
          if (c.section && !isDefault) {
            savedSections.push(c.section);
          }
        });
        setComponentConfigs(initialConfigs);
        if (savedSections.length > 0) {
          setCustomSections((prev) => Array.from(new Set([...prev, ...savedSections])));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custom_earning_data, type, dbSections, defaultSection]);

  useEffect(() => {
    if (type === "Deduction") {
      const saved = custom_deduction_data?.[0]?.deductions_json;
      if (Array.isArray(saved) && saved.length > 0) {
        const normalized = saved.map((c) => ({
          name: c.name,
          label: c.label,
          custom_label: c.custom_label ?? c.label,
          section: c.section || defaultSection,
        }));
        setSelectedComponents(normalized);
        const initialConfigs = {};
        const savedSections = [];
        normalized.forEach((c) => {
          initialConfigs[c.name] = {
            custom_label: c.custom_label,
            section: c.section,
          };
          const isDefault = dbSections.some((s) => s.section_name === c.section && s.is_default);
          if (c.section && !isDefault) {
            savedSections.push(c.section);
          }
        });
        setComponentConfigs(initialConfigs);
        if (savedSections.length > 0) {
          setCustomSections((prev) => Array.from(new Set([...prev, ...savedSections])));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [custom_deduction_data, type, dbSections, defaultSection]);

  const groupedPreviewSections = useMemo(() => {
    const grouped = selectedComponents?.reduce((acc, comp) => {
      const section = comp?.section?.trim() || defaultSection;
      if (!acc[section]) acc[section] = [];
      acc[section].push(comp);
      return acc;
    }, {});
    return Object.entries(grouped || {});
  }, [selectedComponents, defaultSection]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        position: "relative",
      }}
    >
      <Backdrop
        className={classes.Backdrop}
        open={
          (type === "Earning" && (isEarningsLoading || isSaving)) ||
          (type === "Deduction" && (isDeductionLoading || isSaving))
        }
      >
        <CircularProgress />
      </Backdrop>
      <Card sx={{ p: 0 }}>
        <CardHeader
          sx={{
            py: 1,
            px: 2,
            "& .MuiCardHeader-content": { overflow: "hidden" },
          }}
          title={
            <Typography
              variant="subtitle1"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.9rem",
              }}
            >
              {`Choose ${type}s for Payroll`}
              <Chip
                size="small"
                label={`${selectedComponents?.length || "0"} of ${compData?.length} selected`}
              />
            </Typography>
          }
          subheader={
            <Typography variant="caption" color="text.secondary">
              {`Choose which ${type?.toLowerCase()} components will be available as dropdown options`}
            </Typography>
          }
        />
        <CardContent sx={{ pt: 1, mb: -1.5, px: 2 }}>
          {/* Sections Configuration Bar on Top */}
          <Box
            sx={{
              p: 1.2,
              mb: 1.5,
              borderRadius: "8px",
              backgroundColor: "#fcfaff",
              border: "1px solid #ede7f6",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "text.primary",
                }}
              >
                {`${type} Sections`}
              </Typography>
              <Tooltip title={`Manage sections for grouping ${type?.toLowerCase()} components on payslips, reports, and offer letter generation`}>
                <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "pointer" }} />
              </Tooltip>
            </Box>

            {/* Section Chips */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.8,
                alignItems: "center",
              }}
            >
              {availableSections.map((section) => {
                const isDefault = dbSections.some((s) => s.section_name === section && s.is_default);
                const isUsed = isSectionUsed(section) || isDefault;
                return (
                  <Chip
                    key={section}
                    label={section}
                    color="primary"
                    variant="outlined"
                    onDelete={
                      !isUsed
                        ? () => handleDeleteSection(section)
                        : undefined
                    }
                    deleteIcon={
                      !isUsed ? (
                        <Tooltip title="Delete this unused section" arrow>
                          <DeleteIcon fontSize="small" sx={{ fontSize: "16px !important" }} />
                        </Tooltip>
                      ) : undefined
                    }
                    sx={{
                      height: 32,
                      fontSize: "0.8125rem",
                      fontWeight: 500,
                      borderColor: isUsed ? "#d1c4e9" : "#b39ddb",
                      backgroundColor: "#ffffff",
                    }}
                  />
                );
              })}

              {isAddingSection ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    backgroundColor: "#ffffff",
                    border: "1px solid #7c3aed",
                    borderRadius: "16px",
                    px: 1,
                    py: 0.25,
                    height: 32,
                  }}
                >
                  <InputBase
                    autoFocus
                    placeholder="New section name"
                    value={newSectionInput}
                    onChange={(e) => setNewSectionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddSection();
                      } else if (e.key === "Escape") {
                        setIsAddingSection(false);
                        setNewSectionInput("");
                      }
                    }}
                    sx={{
                      fontSize: "0.8125rem",
                      width: 140,
                      "& input": { p: 0 },
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={handleAddSection}
                    sx={{ p: "2px", color: "#7c3aed" }}
                  >
                    <CheckIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setIsAddingSection(false);
                      setNewSectionInput("");
                    }}
                    sx={{ p: "2px", color: "text.secondary" }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : (
                <Chip
                  icon={<AddIcon sx={{ fontSize: "16px !important", color: "#7c3aed" }} />}
                  label="New Section"
                  variant="outlined"
                  onClick={() => {
                    setIsAddingSection(true);
                    setNewSectionInput("");
                  }}
                  sx={{
                    height: 32,
                    cursor: "pointer",
                    borderStyle: "dashed",
                    borderColor: "#7c3aed",
                    color: "#7c3aed",
                    fontWeight: 500,
                    fontSize: "0.8125rem",
                    "& .MuiChip-icon": {
                      marginLeft: "8px",
                      marginRight: "-4px",
                    },
                    "&:hover": {
                      backgroundColor: "rgba(124, 58, 237, 0.08)",
                      borderColor: "#6d28d9",
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Search and Select All */}
          <Box
            sx={{
              display: "flex",
              gap: "8px",
              marginBottom: "8px",
              paddingBottom: "4px",
            }}
          >
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder={`Search ${type?.toLowerCase()}s...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon color="action" sx={{ fontSize: 18, mr: 0.5 }} />
                ),
              }}
            />
            <Box sx={{ width: "160px", display: "flex", alignItems: "center" }}>
              <Checkbox
                checked={
                  selectedComponents?.length === filteredComponents?.length &&
                  filteredComponents?.length > 0
                }
                indeterminate={
                  selectedComponents?.length > 0 &&
                  selectedComponents?.length < filteredComponents?.length
                }
                onChange={handleSelectAll}
                sx={{ padding: "0 4px" }}
                inputProps={{ "aria-label": "Select all components" }}
              />
              <Typography
                variant="body2"
                sx={{ userSelect: "none", color: "inherit" }}
              >
                {selectedComponents?.length === filteredComponents?.length &&
                filteredComponents?.length > 0
                  ? "Clear All"
                  : "Select All"}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              className="mpl-primary-btn"
              sx={{ textTransform: "none", minWidth: 70, height: "36px" }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Apply"}
            </Button>
          </Box>

          <TableContainer
            sx={{
              maxHeight: 290,
              overflowY: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
            }}
          >
            <Table size="small" stickyHeader aria-label={`${type} components table`}>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      fontWeight: 600,
                      backgroundColor: "#f9f9fb",
                      py: 0.8,
                      px: 1,
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ width: "42px" }}>
                    <Checkbox
                      size="small"
                      checked={
                        selectedComponents?.length === filteredComponents?.length &&
                        filteredComponents?.length > 0
                      }
                      indeterminate={
                        selectedComponents?.length > 0 &&
                        selectedComponents?.length < filteredComponents?.length
                      }
                      onChange={handleSelectAll}
                      sx={{ p: 0.5 }}
                      inputProps={{ "aria-label": "Select all rows" }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <span>Current Component</span>
                      <Tooltip title={`Standard ${type?.toLowerCase()} components for payroll calculations`}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 130 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <span>Label</span>
                      <Tooltip title="Custom display name for the component on payslips, reports, and offer letter generation">
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <span>Section</span>
                      <Tooltip title="Group components into sections for payroll, salary templates, and offer letter generation">
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", cursor: "pointer" }} />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ width: "65px", textAlign: "center" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredComponents?.map((component) => {
                  const selectedItem = selectedComponents?.find(
                    (c) => c?.name === component?.name
                  );
                  const isChecked = !!selectedItem;
                  const isEditing = editingRowId === component?.name;
                  const config = componentConfigs[component?.name];
                  const currentLabel =
                    config?.custom_label ??
                    selectedItem?.custom_label ??
                    component?.label;
                  const currentSection =
                    config?.section ??
                    selectedItem?.section ??
                    defaultSection;

                  return (
                    <TableRow
                      key={component?.name}
                      hover
                      sx={{
                        opacity: isChecked ? 1 : 0.65,
                        backgroundColor: isChecked ? "inherit" : "#fafafa",
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ p: 0.5 }}>
                        <Tooltip title="Tick to select component" arrow placement="top">
                          <span>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => handleComponentToggle(component)}
                              sx={{ p: 0.5 }}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        sx={{
                          py: 0.5,
                          px: 1,
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                        onClick={() => handleComponentToggle(component)}
                      >
                        {component?.label}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            variant="standard"
                            fullWidth
                            autoFocus
                            value={currentLabel}
                            onChange={(e) =>
                              handleLabelChange(component, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") setEditingRowId(null);
                            }}
                            placeholder={component?.label || "Component label"}
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: "0.8rem",
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              color: isChecked ? "text.primary" : "text.secondary",
                            }}
                          >
                            {currentLabel || "-"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        {isEditing ? (
                          <Autocomplete
                            freeSolo
                            size="small"
                            value={currentSection}
                            onChange={(event, newValue) => {
                              if (typeof newValue === "string") {
                                handleSectionChange(component, newValue);
                              } else if (newValue && newValue.inputValue) {
                                handleSectionChange(component, newValue.inputValue);
                              } else if (newValue && newValue.title) {
                                handleSectionChange(component, newValue.title);
                              } else {
                                handleSectionChange(component, defaultSection);
                              }
                            }}
                            filterOptions={(options, params) => {
                              const filtered = filter(options, params);
                              const { inputValue } = params;
                              const isExisting = options.some(
                                (option) =>
                                  inputValue.toLowerCase() ===
                                  (option?.title || option)?.toLowerCase()
                              );
                              if (inputValue !== "" && !isExisting) {
                                filtered.push({
                                  inputValue,
                                  title: `Add "${inputValue}"`,
                                });
                              }
                              return filtered;
                            }}
                            selectOnFocus
                            clearOnBlur
                            handleHomeEndKeys
                            options={availableSections.map((s) => ({ title: s }))}
                            getOptionLabel={(option) => {
                              if (typeof option === "string") return option;
                              if (option?.inputValue) return option.inputValue;
                              return option?.title || "";
                            }}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                key={option.inputValue || option.title}
                                style={{ fontSize: "0.75rem" }}
                              >
                                {option.inputValue ? (
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      color: "#883bd5",
                                      fontWeight: 600,
                                    }}
                                  >
                                    <AddIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                    Add "{option.inputValue}"
                                  </span>
                                ) : (
                                  option.title
                                )}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select / Type section"
                                size="small"
                                variant="standard"
                                fullWidth
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") setEditingRowId(null);
                                }}
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "0.8rem",
                                  },
                                }}
                              />
                            )}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.8rem",
                              color: isChecked ? "text.primary" : "text.secondary",
                            }}
                          >
                            {currentSection || "-"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 0.5, textAlign: "center" }}>
                        {isEditing ? (
                          <Box sx={{ display: "flex", justifyContent: "center", gap: "2px" }}>
                            <Tooltip title="Done">
                              <IconButton
                                size="small"
                                onClick={() => setEditingRowId(null)}
                                sx={{ color: "#2e7d32", p: 0.5 }}
                              >
                                <CheckIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Close">
                              <IconButton
                                size="small"
                                onClick={() => setEditingRowId(null)}
                                sx={{ color: "#637381", p: 0.5 }}
                              >
                                <CloseIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Tooltip title="Edit the label and the section name">
                            <IconButton
                              size="small"
                              onClick={() => {
                                if (!isChecked) {
                                  handleComponentToggle(component);
                                }
                                setEditingRowId(component?.name);
                              }}
                              sx={{ color: "#637381", p: 0.5 }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {selectedComponents?.length > 0 && (
            <Accordion
              sx={{
                borderRadius: 2,
                border: "1px solid #ddd",
                backgroundColor: "#f3edfd",
                "&:not(.Mui-expanded)": {
                  border: "none",
                  borderBottom: "1px solid #ddd",
                  mt: 1.5,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: "32px !important",
                  "& .MuiAccordionSummary-content": {
                    margin: "4px 0 !important",
                  },
                  backgroundColor: "#f3edfd",
                }}
              >
                <Typography variant="subtitle2">
                  {`Payroll ${type} Components Dropdown Preview`}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 1, backgroundColor: "#f3edfd" }}>
                <Box
                  sx={{
                    maxHeight: 190,
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  {groupedPreviewSections?.map(([sectionName, items]) => (
                    <Box key={sectionName} sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#5b21b6",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          display: "block",
                          mb: 0.5,
                        }}
                      >
                        {sectionName}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "6px",
                        }}
                      >
                        {items?.map((component) => (
                          <Chip
                            key={component?.name}
                            label={component?.custom_label ?? component?.label}
                            variant="outlined"
                            size="small"
                            sx={{ backgroundColor: "#ffffff" }}
                          />
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  {selectedComponents?.length} {type?.toLowerCase()} components
                  selected - these will show in payroll dropdowns.
                </Typography>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
